import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import CityLaunchManager from "../components/admin/CityLaunchManager";
import ConstructionOpsPanel from "../components/admin/ConstructionOpsPanel";
import { materialSkuDirectory } from "../data/materialSkuDirectory";
import {
  completeServiceBooking,
  raiseBookingDispute,
} from "../utils/marketplaceOps";
import {
  fetchAdminMarketplaceSignals,
  updateServiceBookingLifecycle,
} from "../utils/marketplaceLifecycle";
import { getServiceLifecycleStage } from "../utils/serviceLifecycle";

const getServiceBookingTone = (status) => {
  if (status === "completed") {
    return "healthy";
  }

  if (["cancelled", "declined", "awaiting_manual_assignment"].includes(status)) {
    return "alert";
  }

  if (
    [
      "accepted",
      "in_progress",
      "worker_assigned",
      "worker_reassigned",
      "assigned",
      "en_route",
      "arrived",
      "otp_pending_start",
      "completion_requested",
      "otp_pending_completion",
    ].includes(status)
  ) {
    return "healthy";
  }

  return "warning";
};

function AdminDashboard() {
  const { user, profile, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [stats, setStats] = useState({
    totalBookings: 0,
    totalWorkers: 0,
    totalShopOwners: 0,
    totalCustomers: 0,
    totalMaterialOrders: 0,
    totalMaterials: 0,
    totalMasterSkus: 0,
    openTickets: 0,
  });

  const [recentBookings, setRecentBookings] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentServiceBookings, setRecentServiceBookings] = useState([]);
  const [recentWorkers, setRecentWorkers] = useState([]);
  const [recentShopOwners, setRecentShopOwners] = useState([]);
  const [recentMasterSkus, setRecentMasterSkus] = useState([]);
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [recentMaterialOrders, setRecentMaterialOrders] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [fulfillmentProfiles, setFulfillmentProfiles] = useState([]);
  const [recentRiskEvents, setRecentRiskEvents] = useState([]);
  const [recentPayoutRequests, setRecentPayoutRequests] = useState([]);
  const [recentRecoveryExposures, setRecentRecoveryExposures] = useState([]);
  const [recentFraudCases, setRecentFraudCases] = useState([]);
  const [workerView, setWorkerView] = useState("all");
  const [shopOwnerView, setShopOwnerView] = useState("all");
  const [materialView, setMaterialView] = useState("all");
  const [skuView, setSkuView] = useState("all");
  const [materialSearch, setMaterialSearch] = useState("");
  const [skuSearch, setSkuSearch] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionLoadingKey, setActionLoadingKey] = useState("");
  const [pricingRuleDraft, setPricingRuleDraft] = useState({
    min_margin_amount: "2.00",
    min_margin_percent: "0.08",
    low_cost_advantage_cap: "1.50",
  });
  const [categoryPricingDraft, setCategoryPricingDraft] = useState({
    category: "Hardware",
    min_margin_amount: "3.00",
    min_margin_percent: "0.10",
    low_cost_advantage_cap: "1.00",
  });

  const controlCards = [
    {
      title: "City Control",
      description:
        "Launch, pause, and monitor city-wise availability before field operations scale.",
    },
    {
      title: "Supply Oversight",
      description:
        "Watch worker and shop-owner growth, quality, and operating readiness from one place.",
    },
    {
      title: "Support Queue",
      description:
        "Track user issues, escalations, and trust incidents as the command centre expands.",
    },
    {
      title: "Operations Visibility",
      description:
        "See bookings, material flow, and admin interventions without entering user dashboards.",
    },
  ];

  useEffect(() => {
    if (authLoading) return;

    if (!user || profile?.role !== "admin") {
      setLoading(false);
      return;
    }

    fetchAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, profile?.role]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const [
        bookingsRes,
        workersRes,
        shopOwnersRes,
        customersRes,
        materialOrdersRes,
        materialsRes,
        masterSkuRes,
        ticketsRes,
        recentBookingsRes,
        recentTicketsRes,
        recentServiceBookingsRes,
        recentWorkersRes,
        recentShopOwnersRes,
        recentMasterSkusRes,
        recentMaterialsRes,
        recentMaterialOrdersRes,
        pricingRulesRes,
        fulfillmentProfilesRes,
      ] = await Promise.all([
        supabase.from("bookings").select("id", { count: "exact", head: true }),
        supabase.from("workers").select("id", { count: "exact", head: true }),
        supabase.from("shop_owners").select("id", { count: "exact", head: true }),
        supabase.from("customers").select("id", { count: "exact", head: true }),
        supabase.from("material_orders").select("id", { count: "exact", head: true }),
        supabase.from("materials").select("id", { count: "exact", head: true }),
        supabase
          .from("material_master_skus")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("support_tickets")
          .select("id", { count: "exact", head: true })
          .eq("status", "open"),
        supabase
          .from("bookings")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("support_tickets")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("service_bookings")
          .select(
            "id, order_id, service_title, service_category, booking_date, booking_slot, status, rate, assigned_worker_id, address_text, transport_responsibility, notes, created_at, final_project_value, payment_collected, advance_received, work_completed_percentage, closure_summary, fraud_risk_score, expected_commission_amount, otp_failure_count, closure_edit_count, completion_dispute_count, service_started_at, service_completed_at"
          )
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("workers")
          .select(
            "id, profile_id, full_name, phone, city, online_city, online_status, skills, skill, is_active, live_city_allowed, created_at"
          )
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("shop_owners")
          .select(
            "id, owner_name, business_name, phone, city, gst_number, is_active, live_city_allowed, created_at"
          )
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("material_master_skus")
          .select("id, sku_code, title, category, size_label, updated_at")
          .order("updated_at", { ascending: false })
          .limit(10),
        supabase
          .from("materials")
          .select(
            "id, material_name, category, master_sku_code, quantity, stock_quantity, mrp_price, offered_price, customer_mrp_price, customer_offer_price, platform_margin_amount, platform_margin_percent, price, is_active"
          )
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("material_orders")
          .select(
            "id, order_id, shop_id, assigned_shop_id, material_title, quantity, unit, final_price, delivery_estimate, delivery_status, priority_delivery, assigned_delivery_partner, tracking_id, delivery_city, delivery_address_text, created_at"
          )
          .order("created_at", { ascending: false })
          .limit(10),
        supabase
          .from("material_pricing_rules")
          .select(
            "id, rule_name, scope_type, category, master_sku_id, min_margin_amount, min_margin_percent, low_cost_advantage_cap, is_active, updated_at"
          )
          .eq("is_active", true)
          .order("updated_at", { ascending: false })
          .limit(12),
        supabase
          .from("material_shop_fulfillment_profiles")
          .select(
            "id, shop_id, shop_owner_id, trust_score, rating_score, reliability_score, dispatch_readiness_score, availability_discipline_score, cancellation_penalty, fraud_risk_penalty, last_reviewed_at, updated_at, shops ( shop_name, city )"
          )
          .order("updated_at", { ascending: false })
          .limit(12),
      ]);

      const allResults = [
        bookingsRes,
        workersRes,
        shopOwnersRes,
        customersRes,
        materialOrdersRes,
        materialsRes,
        masterSkuRes,
        ticketsRes,
        recentBookingsRes,
        recentTicketsRes,
        recentServiceBookingsRes,
        recentWorkersRes,
        recentShopOwnersRes,
        recentMasterSkusRes,
        recentMaterialsRes,
        recentMaterialOrdersRes,
        pricingRulesRes,
        fulfillmentProfilesRes,
      ];

      const failed = allResults.find((res) => res.error);
      if (failed?.error) {
        throw failed.error;
      }

      setStats({
        totalBookings: bookingsRes.count || 0,
        totalWorkers: workersRes.count || 0,
        totalShopOwners: shopOwnersRes.count || 0,
        totalCustomers: customersRes.count || 0,
        totalMaterialOrders: materialOrdersRes.count || 0,
        totalMaterials: materialsRes.count || 0,
        totalMasterSkus: masterSkuRes.count || 0,
        openTickets: ticketsRes.count || 0,
      });

      setRecentBookings(recentBookingsRes.data || []);
      setRecentTickets(recentTicketsRes.data || []);
      const recentServiceBookingRows = recentServiceBookingsRes.data || [];
      const recentServiceOrderIds = recentServiceBookingRows
        .map((booking) => booking.order_id)
        .filter(Boolean);

      let recentServiceAddressMap = {};
      let recentServiceOrderCodeMap = {};

      if (recentServiceOrderIds.length > 0) {
        const { data: serviceOrderRows, error: serviceOrderRowsError } =
          await supabase
            .from("orders")
            .select(`
              id,
              order_code,
              addresses (
                house_flat_no,
                street_area,
                city,
                state,
                country,
                pincode
              )
            `)
            .in("id", recentServiceOrderIds);

        if (serviceOrderRowsError) throw serviceOrderRowsError;

        recentServiceAddressMap = (serviceOrderRows || []).reduce(
          (acc, orderRow) => {
            const address = orderRow.addresses;
            acc[orderRow.id] = [
              address?.house_flat_no,
              address?.street_area,
              address?.city,
              address?.state,
              address?.country,
              address?.pincode,
            ]
              .filter(Boolean)
              .join(", ");
            return acc;
          },
          {}
        );

        recentServiceOrderCodeMap = (serviceOrderRows || []).reduce(
          (acc, orderRow) => {
            acc[orderRow.id] = orderRow.order_code || orderRow.id;
            return acc;
          },
          {}
        );
      }

      const recentWorkerRows = recentWorkersRes.data || [];
      const serviceAssignedWorkerIds = [
        ...new Set(
          recentServiceBookingRows
            .map((booking) => booking.assigned_worker_id)
            .filter(Boolean)
        ),
      ];

      let resolvedWorkerRows = recentWorkerRows;

      if (serviceAssignedWorkerIds.length > 0) {
        const { data: additionalWorkerRows, error: additionalWorkerRowsError } =
          await supabase
            .from("workers")
            .select("id, profile_id, full_name, phone")
            .or(
              serviceAssignedWorkerIds
                .map((id) => `id.eq.${id},profile_id.eq.${id}`)
                .join(",")
            );

        if (additionalWorkerRowsError) throw additionalWorkerRowsError;

        const workerMap = new Map();
        [...recentWorkerRows, ...(additionalWorkerRows || [])].forEach((worker) => {
          workerMap.set(worker.id, worker);
        });
        resolvedWorkerRows = Array.from(workerMap.values());
      }

      const recentWorkerMap = resolvedWorkerRows.reduce((acc, worker) => {
        const label =
          worker.full_name ||
          worker.phone ||
          worker.profile_id ||
          worker.id;

        acc[worker.id] = label;
        if (worker.profile_id) {
          acc[worker.profile_id] = label;
        }
        return acc;
      }, {});

      setRecentServiceBookings(
        recentServiceBookingRows.map((booking) => ({
          ...booking,
          orderCode:
            recentServiceOrderCodeMap[booking.order_id] ||
            booking.order_id ||
            "N/A",
          resolvedAddressText:
            booking.address_text || recentServiceAddressMap[booking.order_id] || "",
          assignedWorkerLabel: booking.assigned_worker_id
            ? recentWorkerMap[booking.assigned_worker_id] ||
              booking.assigned_worker_id
            : "Not assigned yet",
        }))
      );
      setRecentWorkers(recentWorkerRows);
      const recentShopOwnerRows = recentShopOwnersRes.data || [];
      const recentMaterialOrderRows = recentMaterialOrdersRes.data || [];
      const materialAssignedShopOwnerIds = [
        ...new Set(
          recentMaterialOrderRows
            .flatMap((order) => [order.assigned_shop_id, order.shop_id])
            .filter(Boolean)
        ),
      ];

      let resolvedShopOwnerRows = recentShopOwnerRows;

      if (materialAssignedShopOwnerIds.length > 0) {
        const {
          data: additionalShopOwnerRows,
          error: additionalShopOwnerRowsError,
        } = await supabase
          .from("shop_owners")
          .select("id, owner_name, business_name, phone")
          .in("id", materialAssignedShopOwnerIds);

        if (additionalShopOwnerRowsError) throw additionalShopOwnerRowsError;

        const shopOwnerMap = new Map();
        [...recentShopOwnerRows, ...(additionalShopOwnerRows || [])].forEach((owner) => {
          shopOwnerMap.set(owner.id, owner);
        });
        resolvedShopOwnerRows = Array.from(shopOwnerMap.values());
      }

      const recentShopOwnerMap = resolvedShopOwnerRows.reduce((acc, owner) => {
        const label =
          owner.business_name ||
          owner.owner_name ||
          owner.phone ||
          owner.id;
        acc[owner.id] = label;
        return acc;
      }, {});
      setRecentShopOwners(recentShopOwnerRows);
      setRecentMasterSkus(recentMasterSkusRes.data || []);
      setRecentMaterials(recentMaterialsRes.data || []);
      setPricingRules(pricingRulesRes.data || []);
      setFulfillmentProfiles(fulfillmentProfilesRes.data || []);
      const globalPricingRule = (pricingRulesRes.data || []).find(
        (rule) => rule.scope_type === "global"
      );
      if (globalPricingRule) {
        setPricingRuleDraft({
          min_margin_amount: String(globalPricingRule.min_margin_amount ?? "2.00"),
          min_margin_percent: String(globalPricingRule.min_margin_percent ?? "0.08"),
          low_cost_advantage_cap: String(
            globalPricingRule.low_cost_advantage_cap ?? "1.50"
          ),
        });
      }
      const recentMaterialOrderIds = recentMaterialOrderRows
        .map((order) => order.order_id)
        .filter(Boolean);

      let recentMaterialAddressMap = {};
      let recentMaterialOrderCodeMap = {};

      if (recentMaterialOrderIds.length > 0) {
        const { data: materialOrderRows, error: materialOrderRowsError } =
          await supabase
            .from("orders")
            .select(`
              id,
              order_code,
              addresses (
                house_flat_no,
                street_area,
                city,
                state,
                country,
                pincode
              )
            `)
            .in("id", recentMaterialOrderIds);

        if (materialOrderRowsError) throw materialOrderRowsError;

        recentMaterialAddressMap = (materialOrderRows || []).reduce(
          (acc, orderRow) => {
            const address = orderRow.addresses;
            acc[orderRow.id] = [
              address?.house_flat_no,
              address?.street_area,
              address?.city,
              address?.state,
              address?.country,
              address?.pincode,
            ]
              .filter(Boolean)
              .join(", ");
            return acc;
          },
          {}
        );

        recentMaterialOrderCodeMap = (materialOrderRows || []).reduce(
          (acc, orderRow) => {
            acc[orderRow.id] = orderRow.order_code || orderRow.id;
            return acc;
          },
          {}
        );
      }

      setRecentMaterialOrders(
        recentMaterialOrderRows.map((order) => ({
          ...order,
          orderCode:
            recentMaterialOrderCodeMap[order.order_id] ||
            order.order_id ||
            "N/A",
          resolvedAddressText:
            order.delivery_address_text ||
            recentMaterialAddressMap[order.order_id] ||
            "",
          assignedShopLabel:
            recentShopOwnerMap[order.assigned_shop_id] ||
            recentShopOwnerMap[order.shop_id] ||
            order.assigned_shop_id ||
            order.shop_id ||
          "Not assigned yet",
        }))
      );

      const marketplaceSignals = await fetchAdminMarketplaceSignals();
      setRecentRiskEvents(marketplaceSignals.riskEvents);
      setRecentPayoutRequests(marketplaceSignals.payoutRequests);
      setRecentRecoveryExposures(marketplaceSignals.recoveryExposures);
      setRecentFraudCases(marketplaceSignals.fraudCases);
    } catch (error) {
      console.error("Admin dashboard error:", error);
      setErrorMessage(error.message || "Failed to load admin dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const handleWorkerToggle = async (worker, field) => {
    const nextValue = !worker[field];
    const actionKey = `worker-${worker.id}-${field}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const { error } = await supabase
        .from("workers")
        .update({ [field]: nextValue })
        .eq("id", worker.id);

      if (error) throw error;

      setRecentWorkers((prev) =>
        prev.map((item) =>
          item.id === worker.id ? { ...item, [field]: nextValue } : item
        )
      );
      setActionMessage(
        `${worker.full_name || "Worker"} updated: ${field} is now ${
          nextValue ? "enabled" : "disabled"
        }.`
      );
    } catch (error) {
      console.error("Worker admin action error:", error);
      setActionMessage(error.message || "Failed to update worker.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleShopOwnerToggle = async (owner, field) => {
    const nextValue = !owner[field];
    const actionKey = `shop-owner-${owner.id}-${field}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const { error } = await supabase
        .from("shop_owners")
        .update({ [field]: nextValue })
        .eq("id", owner.id);

      if (error) throw error;

      setRecentShopOwners((prev) =>
        prev.map((item) =>
          item.id === owner.id ? { ...item, [field]: nextValue } : item
        )
      );
      setActionMessage(
        `${owner.owner_name || "Shop owner"} updated: ${field} is now ${
          nextValue ? "enabled" : "disabled"
        }.`
      );
    } catch (error) {
      console.error("Shop owner admin action error:", error);
      setActionMessage(error.message || "Failed to update shop owner.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleMaterialToggle = async (material, field) => {
    const nextValue = !material[field];
    const actionKey = `material-${material.id}-${field}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const { error } = await supabase
        .from("materials")
        .update({ [field]: nextValue })
        .eq("id", material.id);

      if (error) throw error;

      setRecentMaterials((prev) =>
        prev.map((item) =>
          item.id === material.id ? { ...item, [field]: nextValue } : item
        )
      );
      setActionMessage(
        `${material.material_name || "Material"} updated: ${field} is now ${
          nextValue ? "enabled" : "disabled"
        }.`
      );
    } catch (error) {
      console.error("Material admin action error:", error);
      setActionMessage(error.message || "Failed to update material.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleApplySuggestedSku = async (material, suggestion) => {
    if (!suggestion?.skuCode) return;

    const actionKey = `material-${material.id}-apply-sku`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const { error } = await supabase
        .from("materials")
        .update({ master_sku_code: suggestion.skuCode })
        .eq("id", material.id);

      if (error) throw error;

      setRecentMaterials((prev) =>
        prev.map((item) =>
          item.id === material.id
            ? { ...item, master_sku_code: suggestion.skuCode }
            : item
        )
      );
      setActionMessage(
        `${material.material_name || "Material"} linked to ${suggestion.skuCode}.`
      );
    } catch (error) {
      console.error("Material SKU link error:", error);
      setActionMessage(error.message || "Failed to link material to SKU.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleMaterialOrderStatusUpdate = async (orderId, status) => {
    const actionKey = `material-order-${orderId}-${status}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const { error } = await supabase
        .from("material_orders")
        .update({ delivery_status: status })
        .eq("id", orderId);

      if (error) throw error;

      setRecentMaterialOrders((prev) =>
        prev.map((item) =>
          item.id === orderId ? { ...item, delivery_status: status } : item
        )
      );
      setActionMessage(`Material order updated to ${status.replaceAll("_", " ")}.`);
    } catch (error) {
      console.error("Material order admin action error:", error);
      setActionMessage(error.message || "Failed to update material order.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handlePricingRuleDraftChange = (field, value) => {
    setPricingRuleDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCategoryPricingDraftChange = (field, value) => {
    setCategoryPricingDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveGlobalPricingRule = async () => {
    const globalRule = pricingRules.find((rule) => rule.scope_type === "global");
    if (!globalRule) {
      setActionMessage("Global pricing rule not found.");
      return;
    }

    const actionKey = `pricing-rule-${globalRule.id}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const payload = {
        min_margin_amount: Number(pricingRuleDraft.min_margin_amount || 0),
        min_margin_percent: Number(pricingRuleDraft.min_margin_percent || 0),
        low_cost_advantage_cap: Number(
          pricingRuleDraft.low_cost_advantage_cap || 0
        ),
      };

      const { data, error } = await supabase
        .from("material_pricing_rules")
        .update(payload)
        .eq("id", globalRule.id)
        .select(
          "id, rule_name, scope_type, category, master_sku_id, min_margin_amount, min_margin_percent, low_cost_advantage_cap, is_active, updated_at"
        )
        .single();

      if (error) throw error;

      setPricingRules((prev) =>
        prev.map((rule) => (rule.id === globalRule.id ? data : rule))
      );
      setActionMessage("Global material pricing rule updated.");
    } catch (error) {
      console.error("Pricing rule update error:", error);
      setActionMessage(error.message || "Failed to update pricing rule.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleSaveCategoryPricingRule = async () => {
    const selectedCategory = String(categoryPricingDraft.category || "").trim();
    if (!selectedCategory) {
      setActionMessage("Select a category before saving a category rule.");
      return;
    }

    const existingRule = pricingRules.find(
      (rule) =>
        rule.scope_type === "category" &&
        String(rule.category || "").toLowerCase() === selectedCategory.toLowerCase()
    );

    const payload = {
      rule_name: `${selectedCategory} Pricing Rule`,
      scope_type: "category",
      category: selectedCategory,
      min_margin_amount: Number(categoryPricingDraft.min_margin_amount || 0),
      min_margin_percent: Number(categoryPricingDraft.min_margin_percent || 0),
      low_cost_advantage_cap: Number(
        categoryPricingDraft.low_cost_advantage_cap || 0
      ),
      is_active: true,
    };

    const actionKey = `category-pricing-rule-${selectedCategory}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      let response;

      if (existingRule) {
        response = await supabase
          .from("material_pricing_rules")
          .update(payload)
          .eq("id", existingRule.id)
          .select(
            "id, rule_name, scope_type, category, master_sku_id, min_margin_amount, min_margin_percent, low_cost_advantage_cap, is_active, updated_at"
          )
          .single();
      } else {
        response = await supabase
          .from("material_pricing_rules")
          .insert([payload])
          .select(
            "id, rule_name, scope_type, category, master_sku_id, min_margin_amount, min_margin_percent, low_cost_advantage_cap, is_active, updated_at"
          )
          .single();
      }

      if (response.error) throw response.error;

      setPricingRules((prev) => {
        const others = prev.filter((rule) => rule.id !== response.data.id);
        return [response.data, ...others].sort((a, b) =>
          String(b.updated_at || "").localeCompare(String(a.updated_at || ""))
        );
      });
      setActionMessage(`${selectedCategory} pricing rule saved.`);
    } catch (error) {
      console.error("Category pricing rule update error:", error);
      setActionMessage(error.message || "Failed to save category pricing rule.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleFulfillmentProfileUpdate = async (profileId, field, delta) => {
    const profile = fulfillmentProfiles.find((item) => item.id === profileId);
    if (!profile) return;

    const currentValue = Number(profile[field] || 0);
    const nextValue =
      field.includes("penalty")
        ? Math.max(0, Math.min(100, currentValue + delta))
        : Math.max(0, Math.min(100, currentValue + delta));
    const actionKey = `fulfillment-profile-${profileId}-${field}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const { data, error } = await supabase
        .from("material_shop_fulfillment_profiles")
        .update({
          [field]: nextValue,
          last_reviewed_at: new Date().toISOString(),
        })
        .eq("id", profileId)
        .select(
          "id, shop_id, shop_owner_id, trust_score, rating_score, reliability_score, dispatch_readiness_score, availability_discipline_score, cancellation_penalty, fraud_risk_penalty, last_reviewed_at, updated_at, shops ( shop_name, city )"
        )
        .single();

      if (error) throw error;

      setFulfillmentProfiles((prev) =>
        prev.map((item) => (item.id === profileId ? data : item))
      );
      setActionMessage(
        `${profile.shops?.shop_name || "Shop"} profile updated: ${field} is now ${nextValue}.`
      );
    } catch (error) {
      console.error("Fulfillment profile update error:", error);
      setActionMessage(
        error.message || "Failed to update fulfillment profile."
      );
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleServiceBookingStatusUpdate = async (bookingId, status) => {
    const actionKey = `service-booking-${bookingId}-${status}`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");
      const booking = recentServiceBookings.find((item) => item.id === bookingId);
      let result;

      if (status === "completed") {
        const completionResult = await completeServiceBooking({
          bookingId,
          action: "secure",
          withdrawableAmount: 0,
          notes: "Admin verified completion from dashboard.",
        });

        result = {
          assignmentStatus: "completed",
          financeState: completionResult?.financeState || "secured",
        };
      } else {
        result = await updateServiceBookingLifecycle({
          bookingId,
          workerProfileId: booking?.assigned_worker_id,
          actorProfileId: user?.id,
          status,
          visibleAmount:
            booking?.final_project_value ||
            booking?.rate ||
            booking?.payment_collected ||
            0,
        });
      }

      setRecentServiceBookings((prev) =>
        prev.map((item) =>
          item.id === bookingId
            ? {
                ...item,
                status,
                ...(result.assignmentStatus
                  ? { assignment_status: result.assignmentStatus }
                  : {}),
                ...(result.financeState
                  ? { latestFinanceState: result.financeState }
                  : {}),
              }
            : item
        )
      );
      setActionMessage(`Service booking updated to ${status.replaceAll("_", " ")}.`);
    } catch (error) {
      console.error("Service booking admin action error:", error);
      setActionMessage(error.message || "Failed to update service booking.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const handleServiceBookingDispute = async (booking) => {
    const actionKey = `service-booking-${booking.id}-dispute`;

    try {
      setActionLoadingKey(actionKey);
      setActionMessage("");

      const disputeResult = await raiseBookingDispute({
        bookingId: booking.id,
        disputeType: "admin_review",
        disputedAmount:
          booking?.final_project_value ||
          booking?.payment_collected ||
          booking?.rate ||
          0,
        note: "Admin dispute hold created from dashboard.",
      });

      setRecentServiceBookings((prev) =>
        prev.map((item) =>
          item.id === booking.id
            ? {
                ...item,
                status: "disputed",
                latestFinanceState: disputeResult?.financeState || "dispute_under_review",
              }
            : item
        )
      );

      setActionMessage("Service booking moved to dispute review.");
    } catch (error) {
      console.error("Service booking dispute action error:", error);
      setActionMessage(error.message || "Failed to raise dispute.");
    } finally {
      setActionLoadingKey("");
    }
  };

  const getTransportDisplayLabel = (booking) => {
    if (booking?.transport_responsibility === "customer_arranged") {
      return "Customer will bear transport";
    }

    if (booking?.transport_responsibility === "mistribazaar_arranged") {
      return "MistriBazaar will arrange transport";
    }

    const notes = booking?.notes || "";

    if (notes.includes("Transport responsibility: customer_arranged")) {
      return "Customer will bear transport";
    }

    if (notes.includes("Transport responsibility: mistribazaar_arranged")) {
      return "MistriBazaar will arrange transport";
    }

    return "Transport not specified";
  };

  if (authLoading || loading) {
    return (
      <div style={page}>
        <div style={card}>
          <h1 style={{ marginTop: 0 }}>Admin Command Centre</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || profile?.role !== "admin") {
    return (
      <div style={page}>
        <div style={card}>
          <h1 style={{ marginTop: 0 }}>Access denied</h1>
          <p style={muted}>This command centre is only for admin users.</p>
        </div>
      </div>
    );
  }

  const workerAttentionCount = recentWorkers.filter(
    (worker) =>
      !worker.is_active ||
      !worker.live_city_allowed ||
      !(worker.skills || worker.skill) ||
      !worker.phone
  ).length;

  const shopOwnerAttentionCount = recentShopOwners.filter(
    (owner) =>
      !owner.is_active ||
      !owner.live_city_allowed ||
      !owner.business_name ||
      !owner.phone
  ).length;

  const unlinkedMaterialsCount = recentMaterials.filter(
    (material) => !material.master_sku_code
  ).length;

  const operationalAlerts = [
    {
      label: "Worker Review Queue",
      value: workerAttentionCount,
      tone: workerAttentionCount > 0 ? "alert" : "healthy",
      detail:
        workerAttentionCount > 0
          ? "Recent worker records need admin review."
          : "Recent worker records look operationally healthy.",
    },
    {
      label: "Shop Review Queue",
      value: shopOwnerAttentionCount,
      tone: shopOwnerAttentionCount > 0 ? "alert" : "healthy",
      detail:
        shopOwnerAttentionCount > 0
          ? "Recent shop-owner records need admin review."
          : "Recent shop-owner records look operationally healthy.",
    },
    {
      label: "Catalog Linking Queue",
      value: unlinkedMaterialsCount,
      tone: unlinkedMaterialsCount > 0 ? "warning" : "healthy",
      detail:
        unlinkedMaterialsCount > 0
          ? "Some recent materials are still missing master SKU links."
          : "Recent materials are linked cleanly to the SKU system.",
    },
  ];

  const getWorkerHealth = (worker) => {
    if (!worker.is_active) return { label: "Inactive", tone: "alert" };
    if (!worker.live_city_allowed) {
      return { label: "City Hold", tone: "warning" };
    }
    if (!(worker.skills || worker.skill) || !worker.phone) {
      return { label: "Needs Profile", tone: "warning" };
    }
    if (worker.online_status === "online") {
      return { label: "Live", tone: "healthy" };
    }
    return { label: "Standby", tone: "neutral" };
  };

  const getWorkerAssignmentReadinessGaps = (worker) => {
    const gaps = [];

    if (!worker.is_active) {
      gaps.push("inactive");
    }

    if (!worker.live_city_allowed) {
      gaps.push("city hold");
    }

    if ((worker.online_status || "offline") !== "online") {
      gaps.push("offline");
    }

    if (!(worker.online_city || worker.city)) {
      gaps.push("missing city");
    }

    if (!(worker.skills || worker.skill)) {
      gaps.push("missing skills");
    }

    if (!worker.phone) {
      gaps.push("missing phone");
    }

    return gaps;
  };

  const getShopOwnerHealth = (owner) => {
    if (!owner.is_active) return { label: "Inactive", tone: "alert" };
    if (!owner.live_city_allowed) {
      return { label: "City Hold", tone: "warning" };
    }
    if (!owner.business_name || !owner.phone) {
      return { label: "Needs Profile", tone: "warning" };
    }
    return { label: "Ready", tone: "healthy" };
  };

  const getShopOwnerFulfillmentReadinessGaps = (owner) => {
    const gaps = [];

    if (!owner.is_active) {
      gaps.push("inactive");
    }

    if (!owner.live_city_allowed) {
      gaps.push("city hold");
    }

    if (!owner.business_name) {
      gaps.push("missing business name");
    }

    if (!owner.phone) {
      gaps.push("missing phone");
    }

    if (!owner.city) {
      gaps.push("missing city");
    }

    if (!owner.gst_number) {
      gaps.push("missing GST");
    }

    return gaps;
  };

  const getMaterialHealth = (material) => {
    const quantity = Number(
      material.quantity ?? material.stock_quantity ?? 0
    );
    const price = Number(material.price ?? 0);

    if (material.is_active === false) {
      return { label: "Inactive", tone: "alert" };
    }
    if (!material.master_sku_code) {
      return { label: "Needs SKU Link", tone: "warning" };
    }
    if (!material.material_name || !material.category || price <= 0) {
      return { label: "Needs Pricing", tone: "warning" };
    }
    if (quantity <= 0) {
      return { label: "Out of Stock", tone: "alert" };
    }
    return { label: "Ready", tone: "healthy" };
  };

  const getMaterialSkuSuggestion = (material) => {
    if (!material || material.master_sku_code) return null;

    const materialName = (material.material_name || "").trim().toLowerCase();
    const materialCategory = (material.category || "").trim().toLowerCase();

    if (!materialName && !materialCategory) return null;

    const exactTitleMatch = materialSkuDirectory.find(
      (sku) =>
        sku.title.toLowerCase() === materialName &&
        (!materialCategory ||
          sku.category.toLowerCase() === materialCategory)
    );

    if (exactTitleMatch) {
      return exactTitleMatch;
    }

    const aliasMatch = materialSkuDirectory.find((sku) => {
      const aliasPool = [sku.title, ...(sku.aliases || [])]
        .join(" ")
        .toLowerCase();

      return (
        (!materialCategory ||
          sku.category.toLowerCase() === materialCategory) &&
        (aliasPool.includes(materialName) || materialName.includes(sku.title.toLowerCase()))
      );
    });

    return aliasMatch || null;
  };

  const getSkuHealth = (sku) => {
    if (!sku?.sku_code || !sku?.title) {
      return { label: "Needs Cleanup", tone: "warning" };
    }
    if (!sku?.category || !sku?.size_label) {
      return { label: "Needs Details", tone: "warning" };
    }
    return { label: "Ready", tone: "healthy" };
  };

  const getSkuGaps = (sku) => {
    const gaps = [];

    if (!sku?.sku_code) gaps.push("SKU code");
    if (!sku?.title) gaps.push("title");
    if (!sku?.category) gaps.push("category");
    if (!sku?.size_label) gaps.push("size");

    return gaps;
  };

  const filteredWorkers = recentWorkers.filter((worker) => {
    const tone = getWorkerHealth(worker).tone;
    if (workerView === "review") {
      return tone === "alert" || tone === "warning";
    }
    if (workerView === "ready") {
      return tone === "healthy" || tone === "neutral";
    }
    return true;
  });

  const filteredShopOwners = recentShopOwners.filter((owner) => {
    const tone = getShopOwnerHealth(owner).tone;
    if (shopOwnerView === "review") {
      return tone === "alert" || tone === "warning";
    }
    if (shopOwnerView === "ready") {
      return tone === "healthy";
    }
    return true;
  });

  const filteredMaterials = recentMaterials.filter((material) => {
    const searchQuery = materialSearch.trim().toLowerCase();
    const searchHaystack = [
      material.material_name,
      material.category,
      material.master_sku_code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (searchQuery && !searchHaystack.includes(searchQuery)) {
      return false;
    }

    const tone = getMaterialHealth(material).tone;
    if (materialView === "review") {
      return tone === "alert" || tone === "warning";
    }
    if (materialView === "ready") {
      return tone === "healthy";
    }
    if (materialView === "unlinked") {
      return !material.master_sku_code;
    }
    return true;
  });

  const filteredMasterSkus = recentMasterSkus.filter((sku) => {
    const searchQuery = skuSearch.trim().toLowerCase();
    const searchHaystack = [sku.sku_code, sku.title, sku.category, sku.size_label]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (searchQuery && !searchHaystack.includes(searchQuery)) {
      return false;
    }

    const tone = getSkuHealth(sku).tone;
    if (skuView === "review") {
      return tone === "warning" || tone === "alert";
    }
    if (skuView === "ready") {
      return tone === "healthy";
    }
    return true;
  });

  const skuCategorySummary = filteredMasterSkus.reduce((acc, sku) => {
    const key = sku.category || "Uncategorized";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const materialCategorySummary = filteredMaterials.reduce((acc, material) => {
    const key = material.category || "Uncategorized";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const globalPricingRule = pricingRules.find((rule) => rule.scope_type === "global");
  const categoryPricingRules = pricingRules.filter(
    (rule) => rule.scope_type === "category"
  );
  const categoryOptions = Array.from(
    new Set(
      [...recentMaterials.map((item) => item.category), ...recentMasterSkus.map((item) => item.category)]
        .filter(Boolean)
        .concat(["Hardware", "Electrical", "Plumbing", "Cement", "Steel", "Tiles", "Paint", "Sanitary", "General"])
    )
  ).sort((a, b) => a.localeCompare(b));

  return (
    <div style={page}>
      <div style={heroCard}>
        <h1 style={{ marginTop: 0 }}>Admin Command Centre</h1>
        <p style={heroText}>
          This is the master dashboard for platform-wide control. Monitor
          customers, workers, shop owners, bookings, material orders, support,
          and city operations from one control surface.
        </p>
      </div>

      {errorMessage ? (
        <div style={errorBox}>{errorMessage}</div>
      ) : null}

      {actionMessage ? <div style={actionBox}>{actionMessage}</div> : null}

      <div style={statsGrid}>
        <div style={statCard}>
          <div style={statLabel}>Total Bookings</div>
          <div style={statValue}>{stats.totalBookings}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Total Workers</div>
          <div style={statValue}>{stats.totalWorkers}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Shop Owners</div>
          <div style={statValue}>{stats.totalShopOwners}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Customers</div>
          <div style={statValue}>{stats.totalCustomers}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Material Orders</div>
          <div style={statValue}>{stats.totalMaterialOrders}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Listed Materials</div>
          <div style={statValue}>{stats.totalMaterials}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Master SKUs</div>
          <div style={statValue}>{stats.totalMasterSkus}</div>
        </div>

        <div style={statCard}>
          <div style={statLabel}>Open Tickets</div>
          <div style={statValue}>{stats.openTickets}</div>
        </div>
      </div>

      <div style={commandGrid}>
        {controlCards.map((cardItem) => (
          <div key={cardItem.title} style={commandCard}>
            <div style={commandLabel}>{cardItem.title}</div>
            <div style={commandText}>{cardItem.description}</div>
          </div>
        ))}
      </div>

      <div style={alertGrid}>
        {operationalAlerts.map((alert) => (
          <div key={alert.label} style={card}>
            <div style={alertHeader}>
              <div style={commandLabel}>{alert.label}</div>
              <div style={getBadgeStyle(alert.tone)}>{alert.value}</div>
            </div>
            <p style={commandText}>{alert.detail}</p>
          </div>
        ))}
      </div>

      <div style={sectionGrid}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Recent Bookings</h2>

          {recentBookings.length === 0 ? (
            <p style={muted}>No recent bookings.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {recentBookings.map((booking) => (
                <div key={booking.id} style={innerCard}>
                  <p style={rowText}>
                    <strong>Booking Ref:</strong> {booking.display_user_id || booking.id}
                  </p>
                  <p style={rowText}>
                    <strong>Service:</strong> {booking.service_type || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Date / Slot:</strong> {booking.booking_date || "N/A"} /{" "}
                    {booking.booking_slot || "To be assigned"}
                  </p>
                  <p style={rowText}>
                    <strong>Status:</strong> {booking.status || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>City:</strong> {booking.city || "N/A"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Recent Support Tickets</h2>

          {recentTickets.length === 0 ? (
            <p style={muted}>No support tickets.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {recentTickets.map((ticket) => (
                <div key={ticket.id} style={innerCard}>
                  <p style={rowText}>
                    <strong>Subject:</strong> {ticket.subject || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Status:</strong> {ticket.status || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Message:</strong> {ticket.message || "N/A"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={sectionGrid}>
        <div style={card}>
          <div style={sectionHeaderRow}>
            <h2 style={{ marginTop: 0, marginBottom: 0 }}>Recent Workers</h2>
            <div style={filterRow}>
              <button
                type="button"
                onClick={() => setWorkerView("all")}
                style={getFilterButtonStyle(workerView === "all")}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setWorkerView("review")}
                style={getFilterButtonStyle(workerView === "review")}
              >
                Needs Review
              </button>
              <button
                type="button"
                onClick={() => setWorkerView("ready")}
                style={getFilterButtonStyle(workerView === "ready")}
              >
                Ready
              </button>
            </div>
          </div>

          {filteredWorkers.length === 0 ? (
            <p style={muted}>No workers match this view yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {filteredWorkers.map((worker) => (
                <div key={worker.id} style={innerCard}>
                  {(() => {
                    const readinessGaps = getWorkerAssignmentReadinessGaps(worker);

                    return (
                      <>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {worker.full_name || "Worker"}
                    </strong>
                    <div style={getBadgeStyle(getWorkerHealth(worker).tone)}>
                      {getWorkerHealth(worker).label}
                    </div>
                  </div>
                  <p style={rowText}>
                    <strong>Phone:</strong> {worker.phone || "Not added"}
                  </p>
                  <p style={rowText}>
                    <strong>City:</strong>{" "}
                    {worker.online_city || worker.city || "Not set"}
                  </p>
                  <p style={rowText}>
                    <strong>Skills:</strong>{" "}
                    {worker.skills || worker.skill || "Not added"}
                  </p>
                  <p style={rowText}>
                    <strong>Status:</strong> {worker.online_status || "offline"}
                  </p>
                  <p style={rowText}>
                    <strong>Active:</strong> {worker.is_active ? "Yes" : "No"}
                  </p>
                  <p style={rowText}>
                    <strong>Live City Allowed:</strong>{" "}
                    {worker.live_city_allowed ? "Yes" : "No"}
                  </p>
                  <p style={rowText}>
                    <strong>Assignment Readiness:</strong>{" "}
                    {readinessGaps.length === 0
                      ? "Ready for auto-assignment"
                      : `Blocked by ${readinessGaps.join(", ")}`}
                  </p>
                  <div style={actionRow}>
                    <button
                      type="button"
                      onClick={() => handleWorkerToggle(worker, "is_active")}
                      disabled={actionLoadingKey === `worker-${worker.id}-is_active`}
                      style={getActionButtonStyle("alert")}
                    >
                      {actionLoadingKey === `worker-${worker.id}-is_active`
                        ? "Saving..."
                        : worker.is_active
                        ? "Pause Worker"
                        : "Activate Worker"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleWorkerToggle(worker, "live_city_allowed")
                      }
                      disabled={
                        actionLoadingKey ===
                        `worker-${worker.id}-live_city_allowed`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      {actionLoadingKey ===
                      `worker-${worker.id}-live_city_allowed`
                        ? "Saving..."
                        : worker.live_city_allowed
                        ? "City Hold"
                        : "Release City Hold"}
                    </button>
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={sectionHeaderRow}>
            <h2 style={{ marginTop: 0, marginBottom: 0 }}>
              Recent Shop Owners
            </h2>
            <div style={filterRow}>
              <button
                type="button"
                onClick={() => setShopOwnerView("all")}
                style={getFilterButtonStyle(shopOwnerView === "all")}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setShopOwnerView("review")}
                style={getFilterButtonStyle(shopOwnerView === "review")}
              >
                Needs Review
              </button>
              <button
                type="button"
                onClick={() => setShopOwnerView("ready")}
                style={getFilterButtonStyle(shopOwnerView === "ready")}
              >
                Ready
              </button>
            </div>
          </div>

          {filteredShopOwners.length === 0 ? (
            <p style={muted}>No shop owners match this view yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {filteredShopOwners.map((owner) => (
                <div key={owner.id} style={innerCard}>
                  {(() => {
                    const readinessGaps = getShopOwnerFulfillmentReadinessGaps(owner);

                    return (
                      <>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {owner.owner_name || "Shop owner"}
                    </strong>
                    <div style={getBadgeStyle(getShopOwnerHealth(owner).tone)}>
                      {getShopOwnerHealth(owner).label}
                    </div>
                  </div>
                  <p style={rowText}>
                    <strong>Business:</strong>{" "}
                    {owner.business_name || "Not added"}
                  </p>
                  <p style={rowText}>
                    <strong>Phone:</strong> {owner.phone || "Not added"}
                  </p>
                  <p style={rowText}>
                    <strong>City:</strong> {owner.city || "Not set"}
                  </p>
                  <p style={rowText}>
                    <strong>GST:</strong> {owner.gst_number || "Not added"}
                  </p>
                  <p style={rowText}>
                    <strong>Active:</strong> {owner.is_active ? "Yes" : "No"}
                  </p>
                  <p style={rowText}>
                    <strong>Live City Allowed:</strong>{" "}
                    {owner.live_city_allowed ? "Yes" : "No"}
                  </p>
                  <p style={rowText}>
                    <strong>Fulfillment Readiness:</strong>{" "}
                    {readinessGaps.length === 0
                      ? "Ready for live material fulfillment"
                      : `Blocked by ${readinessGaps.join(", ")}`}
                  </p>
                  <div style={actionRow}>
                    <button
                      type="button"
                      onClick={() =>
                        handleShopOwnerToggle(owner, "is_active")
                      }
                      disabled={
                        actionLoadingKey === `shop-owner-${owner.id}-is_active`
                      }
                      style={getActionButtonStyle("alert")}
                    >
                      {actionLoadingKey ===
                      `shop-owner-${owner.id}-is_active`
                        ? "Saving..."
                        : owner.is_active
                        ? "Pause Shop"
                        : "Activate Shop"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleShopOwnerToggle(owner, "live_city_allowed")
                      }
                      disabled={
                        actionLoadingKey ===
                        `shop-owner-${owner.id}-live_city_allowed`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      {actionLoadingKey ===
                      `shop-owner-${owner.id}-live_city_allowed`
                        ? "Saving..."
                        : owner.live_city_allowed
                        ? "City Hold"
                        : "Release City Hold"}
                    </button>
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={sectionGrid}>
        <div style={card}>
          <div style={sectionHeaderRow}>
            <div>
              <h2 style={{ margin: 0 }}>Marketplace Pricing Rule</h2>
              <p style={{ ...muted, marginBottom: 0 }}>
                This global rule decides how aggressively MistriBazaar protects margin while staying below MRP.
              </p>
            </div>
            <div style={getBadgeStyle(globalPricingRule ? "healthy" : "warning")}>
              {globalPricingRule ? "Global Rule Live" : "Rule Missing"}
            </div>
          </div>

          <div style={twoColumnAdminGrid}>
            <label style={fieldLabel}>
              Minimum Margin Amount
              <input
                value={pricingRuleDraft.min_margin_amount}
                onChange={(e) =>
                  handlePricingRuleDraftChange("min_margin_amount", e.target.value)
                }
                style={searchInput}
              />
            </label>

            <label style={fieldLabel}>
              Minimum Margin Percent
              <input
                value={pricingRuleDraft.min_margin_percent}
                onChange={(e) =>
                  handlePricingRuleDraftChange("min_margin_percent", e.target.value)
                }
                style={searchInput}
              />
            </label>

            <label style={fieldLabel}>
              Low-Cost Advantage Cap
              <input
                value={pricingRuleDraft.low_cost_advantage_cap}
                onChange={(e) =>
                  handlePricingRuleDraftChange(
                    "low_cost_advantage_cap",
                    e.target.value
                  )
                }
                style={searchInput}
              />
            </label>
          </div>

          <div style={actionRow}>
            <button
              type="button"
              onClick={handleSaveGlobalPricingRule}
              disabled={
                !globalPricingRule ||
                actionLoadingKey === `pricing-rule-${globalPricingRule?.id}`
              }
              style={getActionButtonStyle("healthy")}
            >
              {actionLoadingKey === `pricing-rule-${globalPricingRule?.id}`
                ? "Saving..."
                : "Save Global Rule"}
            </button>
          </div>

          {pricingRules.length > 0 ? (
            <div style={{ display: "grid", gap: "10px", marginTop: "16px" }}>
              {pricingRules.slice(0, 4).map((rule) => (
                <div key={rule.id} style={innerCard}>
                  <p style={rowText}>
                    <strong>{rule.rule_name}</strong> | {rule.scope_type}
                    {rule.category ? ` | ${rule.category}` : ""}
                  </p>
                  <p style={rowText}>
                    Margin Amount: ₹{rule.min_margin_amount} | Margin %:{" "}
                    {(Number(rule.min_margin_percent || 0) * 100).toFixed(2)}% |
                    Low-Cost Cap: ₹{rule.low_cost_advantage_cap}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={muted}>No pricing rules loaded.</p>
          )}

          <div style={dividerLine} />

          <h3 style={{ marginTop: 0 }}>Category Pricing Controls</h3>
          <div style={twoColumnAdminGrid}>
            <label style={fieldLabel}>
              Category
              <select
                value={categoryPricingDraft.category}
                onChange={(e) =>
                  handleCategoryPricingDraftChange("category", e.target.value)
                }
                style={searchInput}
              >
                {categoryOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>

            <label style={fieldLabel}>
              Category Margin Amount
              <input
                value={categoryPricingDraft.min_margin_amount}
                onChange={(e) =>
                  handleCategoryPricingDraftChange(
                    "min_margin_amount",
                    e.target.value
                  )
                }
                style={searchInput}
              />
            </label>

            <label style={fieldLabel}>
              Category Margin Percent
              <input
                value={categoryPricingDraft.min_margin_percent}
                onChange={(e) =>
                  handleCategoryPricingDraftChange(
                    "min_margin_percent",
                    e.target.value
                  )
                }
                style={searchInput}
              />
            </label>

            <label style={fieldLabel}>
              Category Low-Cost Cap
              <input
                value={categoryPricingDraft.low_cost_advantage_cap}
                onChange={(e) =>
                  handleCategoryPricingDraftChange(
                    "low_cost_advantage_cap",
                    e.target.value
                  )
                }
                style={searchInput}
              />
            </label>
          </div>

          <div style={actionRow}>
            <button
              type="button"
              onClick={handleSaveCategoryPricingRule}
              disabled={
                actionLoadingKey ===
                `category-pricing-rule-${categoryPricingDraft.category}`
              }
              style={getActionButtonStyle("warning")}
            >
              {actionLoadingKey ===
              `category-pricing-rule-${categoryPricingDraft.category}`
                ? "Saving..."
                : "Save Category Rule"}
            </button>
          </div>

          {categoryPricingRules.length > 0 ? (
            <div style={{ display: "grid", gap: "10px", marginTop: "16px" }}>
              {categoryPricingRules.slice(0, 6).map((rule) => (
                <div key={rule.id} style={innerCard}>
                  <p style={rowText}>
                    <strong>{rule.category}</strong> pricing rule
                  </p>
                  <p style={rowText}>
                    Margin Amount: ₹{rule.min_margin_amount} | Margin %:{" "}
                    {(Number(rule.min_margin_percent || 0) * 100).toFixed(2)}% |
                    Low-Cost Cap: ₹{rule.low_cost_advantage_cap}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div style={card}>
          <div style={sectionHeaderRow}>
            <div>
              <h2 style={{ margin: 0 }}>Fulfillment Governance</h2>
              <p style={{ ...muted, marginBottom: 0 }}>
                Fulfillment profiles help us prefer shops that are still profitable but more operationally reliable.
              </p>
            </div>
            <div style={getBadgeStyle(fulfillmentProfiles.length ? "healthy" : "warning")}>
              {fulfillmentProfiles.length} Profiles
            </div>
          </div>

          {fulfillmentProfiles.length === 0 ? (
            <p style={muted}>No fulfillment profiles loaded yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {fulfillmentProfiles.slice(0, 6).map((profile) => (
                <div key={profile.id} style={innerCard}>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {profile.shops?.shop_name || "Shop"}
                    </strong>
                    <div style={getBadgeStyle("neutral")}>
                      {profile.shops?.city || "City N/A"}
                    </div>
                  </div>

                  <p style={rowText}>
                    Trust {profile.trust_score} | Rating {profile.rating_score} |
                    Reliability {profile.reliability_score}
                  </p>
                  <p style={rowText}>
                    Dispatch {profile.dispatch_readiness_score} | Availability{" "}
                    {profile.availability_discipline_score}
                  </p>
                  <p style={rowText}>
                    Cancellation Penalty {profile.cancellation_penalty} | Fraud
                    Penalty {profile.fraud_risk_penalty}
                  </p>

                  <div style={actionRow}>
                    <button
                      type="button"
                      onClick={() =>
                        handleFulfillmentProfileUpdate(
                          profile.id,
                          "reliability_score",
                          5
                        )
                      }
                      disabled={
                        actionLoadingKey ===
                        `fulfillment-profile-${profile.id}-reliability_score`
                      }
                      style={getActionButtonStyle("healthy")}
                    >
                      Boost Reliability
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleFulfillmentProfileUpdate(
                          profile.id,
                          "dispatch_readiness_score",
                          5
                        )
                      }
                      disabled={
                        actionLoadingKey ===
                        `fulfillment-profile-${profile.id}-dispatch_readiness_score`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      Boost Dispatch
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleFulfillmentProfileUpdate(
                          profile.id,
                          "availability_discipline_score",
                          5
                        )
                      }
                      disabled={
                        actionLoadingKey ===
                        `fulfillment-profile-${profile.id}-availability_discipline_score`
                      }
                      style={getActionButtonStyle("healthy")}
                    >
                      Boost Availability
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleFulfillmentProfileUpdate(
                          profile.id,
                          "fraud_risk_penalty",
                          5
                        )
                      }
                      disabled={
                        actionLoadingKey ===
                        `fulfillment-profile-${profile.id}-fraud_risk_penalty`
                      }
                      style={getActionButtonStyle("alert")}
                    >
                      Raise Fraud Penalty
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleFulfillmentProfileUpdate(
                          profile.id,
                          "fraud_risk_penalty",
                          -5
                        )
                      }
                      disabled={
                        actionLoadingKey ===
                        `fulfillment-profile-${profile.id}-fraud_risk_penalty`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      Reduce Fraud Penalty
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={sectionGrid}>
        <div style={card}>
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Recent Service Bookings
          </h2>

          {recentServiceBookings.length === 0 ? (
            <p style={muted}>No service bookings yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {recentServiceBookings.map((booking) => (
                <div key={booking.id} style={innerCard}>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {booking.service_title || "Service Booking"}
                    </strong>
                    <div
                      style={getBadgeStyle(getServiceBookingTone(booking.status))}
                    >
                      {getServiceLifecycleStage(booking.status || "pending_assignment")}
                    </div>
                  </div>
                  <p style={rowText}>
                    <strong>Category:</strong> {booking.service_category || "general"}
                  </p>
                  <p style={rowText}>
                    <strong>Order ID:</strong> {booking.orderCode}
                  </p>
                  <p style={rowText}>
                    <strong>Date:</strong> {booking.booking_date || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Slot:</strong> {booking.booking_slot || "To be assigned"}
                  </p>
                  <p style={rowText}>
                    <strong>Assigned Worker:</strong>{" "}
                    {booking.assignedWorkerLabel || "Not assigned yet"}
                  </p>
                  <p style={rowText}>
                    <strong>Assignment State:</strong>{" "}
                    {String(
                      booking.assignment_status ||
                        (booking.status === "awaiting_manual_assignment"
                          ? "manual_assignment_required"
                          : "pending")
                    )
                      .replaceAll("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                  </p>
                  <p style={rowText}>
                    <strong>Transport:</strong> {getTransportDisplayLabel(booking)}
                  </p>
                  <p style={rowText}>
                    <strong>Address:</strong>{" "}
                    {booking.resolvedAddressText || booking.address_text || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Rate:</strong> ₹{booking.rate || 0}
                  </p>
                  <div style={actionRow}>
                    <button
                      type="button"
                      onClick={() =>
                        handleServiceBookingStatusUpdate(booking.id, "accepted")
                      }
                      disabled={
                        actionLoadingKey ===
                        `service-booking-${booking.id}-accepted`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      {actionLoadingKey ===
                      `service-booking-${booking.id}-accepted`
                        ? "Saving..."
                        : "Accept"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleServiceBookingStatusUpdate(
                          booking.id,
                          "in_progress"
                        )
                      }
                      disabled={
                        actionLoadingKey ===
                        `service-booking-${booking.id}-in_progress`
                      }
                      style={getActionButtonStyle("healthy")}
                    >
                      {actionLoadingKey ===
                      `service-booking-${booking.id}-in_progress`
                        ? "Saving..."
                        : "Start"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleServiceBookingStatusUpdate(booking.id, "completed")
                      }
                      disabled={
                        actionLoadingKey ===
                        `service-booking-${booking.id}-completed`
                      }
                      style={getActionButtonStyle("healthy")}
                    >
                      {actionLoadingKey ===
                      `service-booking-${booking.id}-completed`
                        ? "Saving..."
                        : "Complete"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleServiceBookingDispute(booking)}
                      disabled={
                        actionLoadingKey ===
                        `service-booking-${booking.id}-dispute`
                      }
                      style={getActionButtonStyle("alert")}
                    >
                      {actionLoadingKey ===
                      `service-booking-${booking.id}-dispute`
                        ? "Saving..."
                        : "Dispute Hold"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
            Recent Material Orders
          </h2>

          {recentMaterialOrders.length === 0 ? (
            <p style={muted}>No material orders yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {recentMaterialOrders.map((order) => (
                <div key={order.id} style={innerCard}>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {order.material_title || "Material Order"}
                    </strong>
                    <div
                      style={getBadgeStyle(
                        ["delivered", "completed"].includes(order.delivery_status)
                          ? "healthy"
                          : ["cancelled", "failed", "rejected"].includes(order.delivery_status)
                          ? "alert"
                          : "warning"
                      )}
                    >
                      {String(order.delivery_status || "awaiting_shop_confirmation")
                        .replaceAll("_", " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                    </div>
                  </div>
                  <p style={rowText}>
                    <strong>Order ID:</strong> {order.orderCode}
                  </p>
                  <p style={rowText}>
                    <strong>Quantity:</strong> {order.quantity} {order.unit || "unit"}
                  </p>
                  <p style={rowText}>
                    <strong>Assigned Shop:</strong> {order.assignedShopLabel}
                  </p>
                  <p style={rowText}>
                    <strong>Amount:</strong> ₹{order.final_price || 0}
                  </p>
                  <p style={rowText}>
                    <strong>Delivery Estimate:</strong>{" "}
                    {order.delivery_estimate || "Awaiting estimate"}
                  </p>
                  <p style={rowText}>
                    <strong>Address:</strong>{" "}
                    {order.resolvedAddressText || "Address not available yet"}
                  </p>
                  <p style={rowText}>
                    <strong>Delivery Partner:</strong>{" "}
                    {order.assigned_delivery_partner || "Not assigned yet"}
                  </p>
                  <p style={rowText}>
                    <strong>Tracking ID:</strong> {order.tracking_id || "Not generated"}
                  </p>
                  <p style={rowText}>
                    <strong>Priority Delivery:</strong>{" "}
                    {order.priority_delivery ? "Yes" : "No"}
                  </p>
                  <div style={actionRow}>
                    <button
                      type="button"
                      onClick={() =>
                        handleMaterialOrderStatusUpdate(order.id, "shop_confirmed")
                      }
                      disabled={
                        actionLoadingKey === `material-order-${order.id}-shop_confirmed`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      {actionLoadingKey === `material-order-${order.id}-shop_confirmed`
                        ? "Saving..."
                        : "Confirm"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleMaterialOrderStatusUpdate(order.id, "preparing_dispatch")
                      }
                      disabled={
                        actionLoadingKey ===
                        `material-order-${order.id}-preparing_dispatch`
                      }
                      style={getActionButtonStyle("warning")}
                    >
                      {actionLoadingKey ===
                      `material-order-${order.id}-preparing_dispatch`
                        ? "Saving..."
                        : "Preparing"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleMaterialOrderStatusUpdate(order.id, "out_for_delivery")
                      }
                      disabled={
                        actionLoadingKey ===
                        `material-order-${order.id}-out_for_delivery`
                      }
                      style={getActionButtonStyle("healthy")}
                    >
                      {actionLoadingKey ===
                      `material-order-${order.id}-out_for_delivery`
                        ? "Saving..."
                        : "Out for Delivery"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleMaterialOrderStatusUpdate(order.id, "delivered")
                      }
                      disabled={
                        actionLoadingKey === `material-order-${order.id}-delivered`
                      }
                      style={getActionButtonStyle("healthy")}
                    >
                      {actionLoadingKey === `material-order-${order.id}-delivered`
                        ? "Saving..."
                        : "Delivered"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={sectionHeaderRow}>
            <h2 style={{ marginTop: 0, marginBottom: 0 }}>Recent Master SKUs</h2>
            <div style={filterRow}>
              <button
                type="button"
                onClick={() => setSkuView("all")}
                style={getFilterButtonStyle(skuView === "all")}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setSkuView("review")}
                style={getFilterButtonStyle(skuView === "review")}
              >
                Needs Review
              </button>
              <button
                type="button"
                onClick={() => setSkuView("ready")}
                style={getFilterButtonStyle(skuView === "ready")}
              >
                Ready
              </button>
            </div>
          </div>

          <input
            type="text"
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            placeholder="Search SKU code, title, category, size"
            style={searchInput}
          />

          <div style={summaryRow}>
            {Object.keys(skuCategorySummary).length === 0 ? (
              <p style={muted}>No category summary available.</p>
            ) : (
              Object.entries(skuCategorySummary).map(([category, count]) => (
                <div key={category} style={summaryPill}>
                  <strong>{category}</strong>: {count}
                </div>
              ))
            )}
          </div>

          {filteredMasterSkus.length === 0 ? (
            <p style={muted}>No master SKU records yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {filteredMasterSkus.map((sku) => (
                <div key={sku.id} style={innerCard}>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {sku.sku_code || "SKU"}
                    </strong>
                    <div style={getBadgeStyle(getSkuHealth(sku).tone)}>
                      {getSkuHealth(sku).label}
                    </div>
                  </div>
                  <p style={rowText}>
                    <strong>Title:</strong> {sku.title || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Category:</strong> {sku.category || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Size:</strong> {sku.size_label || "N/A"}
                  </p>
                  {getSkuGaps(sku).length > 0 ? (
                    <div style={suggestionBox}>
                      <p style={{ ...rowText, marginTop: 0, marginBottom: "6px" }}>
                        <strong>Missing Details:</strong>
                      </p>
                      <div style={summaryRow}>
                        {getSkuGaps(sku).map((gap) => (
                          <div key={gap} style={gapPill}>
                            {gap}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={sectionHeaderRow}>
            <h2 style={{ marginTop: 0, marginBottom: 0 }}>
              Recent Listed Materials
            </h2>
            <div style={filterRow}>
              <button
                type="button"
                onClick={() => setMaterialView("all")}
                style={getFilterButtonStyle(materialView === "all")}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setMaterialView("review")}
                style={getFilterButtonStyle(materialView === "review")}
              >
                Needs Review
              </button>
              <button
                type="button"
                onClick={() => setMaterialView("unlinked")}
                style={getFilterButtonStyle(materialView === "unlinked")}
              >
                Unlinked
              </button>
              <button
                type="button"
                onClick={() => setMaterialView("ready")}
                style={getFilterButtonStyle(materialView === "ready")}
              >
                Ready
              </button>
            </div>
          </div>

          <input
            type="text"
            value={materialSearch}
            onChange={(e) => setMaterialSearch(e.target.value)}
            placeholder="Search material name, category, linked SKU"
            style={searchInput}
          />

          <div style={summaryRow}>
            {Object.keys(materialCategorySummary).length === 0 ? (
              <p style={muted}>No material category summary available.</p>
            ) : (
              Object.entries(materialCategorySummary).map(([category, count]) => (
                <div key={category} style={summaryPill}>
                  <strong>{category}</strong>: {count}
                </div>
              ))
            )}
          </div>

          {filteredMaterials.length === 0 ? (
            <p style={muted}>No materials match this view yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {filteredMaterials.map((material) => (
                <div key={material.id} style={innerCard}>
                  {(() => {
                    const skuSuggestion = getMaterialSkuSuggestion(material);

                    return (
                      <>
                  <div style={cardHeaderRow}>
                    <strong style={{ color: "#fff" }}>
                      {material.material_name || "Material"}
                    </strong>
                    <div style={getBadgeStyle(getMaterialHealth(material).tone)}>
                      {getMaterialHealth(material).label}
                    </div>
                  </div>
                  <p style={rowText}>
                    <strong>Master SKU:</strong> {material.master_sku_code || "Not linked"}
                  </p>
                  <p style={rowText}>
                    <strong>Category:</strong> {material.category || "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Qty:</strong>{" "}
                    {material.quantity ?? material.stock_quantity ?? "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Seller MRP:</strong> {material.mrp_price ?? material.price ?? "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Seller Offered:</strong> {material.offered_price ?? material.price ?? "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Customer MRP:</strong> {material.customer_mrp_price ?? "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Customer Offered:</strong> {material.customer_offer_price ?? "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Platform Margin:</strong> {material.platform_margin_amount ?? "N/A"}
                  </p>
                  <p style={rowText}>
                    <strong>Active:</strong> {material.is_active === false ? "No" : "Yes"}
                  </p>
                  {skuSuggestion ? (
                    <div style={suggestionBox}>
                      <p style={{ ...rowText, marginTop: 0 }}>
                        <strong>Suggested SKU:</strong> {skuSuggestion.skuCode}
                      </p>
                      <p style={{ ...rowText, marginBottom: 0 }}>
                        {skuSuggestion.title}
                      </p>
                    </div>
                  ) : null}
                  <div style={actionRow}>
                    {skuSuggestion ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleApplySuggestedSku(material, skuSuggestion)
                        }
                        disabled={
                          actionLoadingKey ===
                          `material-${material.id}-apply-sku`
                        }
                        style={getActionButtonStyle("healthy")}
                      >
                        {actionLoadingKey ===
                        `material-${material.id}-apply-sku`
                          ? "Linking..."
                          : "Apply SKU Link"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleMaterialToggle(material, "is_active")}
                      disabled={
                        actionLoadingKey === `material-${material.id}-is_active`
                      }
                      style={getActionButtonStyle("alert")}
                    >
                      {actionLoadingKey === `material-${material.id}-is_active`
                        ? "Saving..."
                        : material.is_active === false
                        ? "Activate Listing"
                        : "Pause Listing"}
                    </button>
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CityLaunchManager />

      <ConstructionOpsPanel recentServiceBookings={recentServiceBookings} />

      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Marketplace Finance Signals</h2>
        <div style={{ display: "grid", gap: "12px" }}>
          <div style={innerCard}>
            <strong style={{ color: "#fff" }}>Recent Risk Events</strong>
            {recentRiskEvents.length === 0 ? (
              <p style={muted}>No v2 risk events available yet.</p>
            ) : (
              recentRiskEvents.slice(0, 6).map((event) => (
                <p key={event.id} style={rowText}>
                  <strong>{String(event.event_type || "risk_event").replaceAll("_", " ")}</strong>
                  {" "}• Severity {event.severity || "medium"}
                  {event.action_taken ? ` • Action ${event.action_taken}` : ""}
                </p>
              ))
            )}
          </div>
          <div style={innerCard}>
            <strong style={{ color: "#fff" }}>Recent Payout Requests</strong>
            {recentPayoutRequests.length === 0 ? (
              <p style={muted}>No v2 payout requests available yet.</p>
            ) : (
              recentPayoutRequests.slice(0, 6).map((request) => (
                <p key={request.id} style={rowText}>
                  <strong>{String(request.status || "draft").replaceAll("_", " ")}</strong>
                  {` • Rs ${Number(request.amount_approved || request.amount_requested || 0).toLocaleString("en-IN")}`}
                  {request.request_type ? ` • ${String(request.request_type).replaceAll("_", " ")}` : ""}
                </p>
              ))
            )}
          </div>
          <div style={innerCard}>
            <strong style={{ color: "#fff" }}>Recovery Queue</strong>
            {recentRecoveryExposures.length === 0 ? (
              <p style={muted}>No recovery-mode exposure rows available yet.</p>
            ) : (
              recentRecoveryExposures.slice(0, 6).map((exposure) => (
                <p key={exposure.id} style={rowText}>
                  <strong>{String(exposure.exposure_type || "exposure").replaceAll("_", " ")}</strong>
                  {` • Rs ${Number(exposure.outstanding_amount || 0).toLocaleString("en-IN")}`}
                  {exposure.aging_bucket ? ` • ${String(exposure.aging_bucket).replaceAll("_", " ")}` : ""}
                  {exposure.risk_level ? ` • ${exposure.risk_level}` : ""}
                </p>
              ))
            )}
          </div>
          <div style={innerCard}>
            <strong style={{ color: "#fff" }}>Fraud Case Panel</strong>
            {recentFraudCases.length === 0 ? (
              <p style={muted}>No settlement fraud cases available yet.</p>
            ) : (
              recentFraudCases.slice(0, 6).map((fraudCase) => (
                <p key={fraudCase.id} style={rowText}>
                  <strong>{String(fraudCase.case_type || "fraud_case").replaceAll("_", " ")}</strong>
                  {fraudCase.severity ? ` • ${fraudCase.severity}` : ""}
                  {fraudCase.status ? ` • ${fraudCase.status}` : ""}
                  {` • Rs ${Number(fraudCase.estimated_loss_amount || 0).toLocaleString("en-IN")}`}
                </p>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Planned Admin Controls</h2>
        <ul style={list}>
          <li>Manual worker assignment</li>
          <li>Manual shop reassignment</li>
          <li>Master SKU and materials catalog control</li>
          <li>Delivery partner fallback control</li>
          <li>Wallet and earnings monitoring</li>
          <li>Waybill and tracking management</li>
          <li>Customer support workflow</li>
        </ul>
      </div>
    </div>
  );
}

const page = {
  background: "#000",
  minHeight: "100vh",
  padding: "40px",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
  maxWidth: "1200px",
  margin: "0 auto",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  border: "1px solid #222",
  borderRadius: "22px",
  padding: "28px",
  marginBottom: "24px",
};

const heroText = {
  color: "#d1d1d1",
  lineHeight: "1.8",
  marginBottom: 0,
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "16px",
  marginBottom: "24px",
};

const statCard = {
  background: "#111",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "20px",
};

const statLabel = {
  color: "#9f9f9f",
  fontSize: "13px",
  marginBottom: "8px",
};

const statValue = {
  fontSize: "28px",
  fontWeight: "bold",
};

const sectionGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "18px",
  marginBottom: "24px",
};

const alertGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
  marginBottom: "24px",
};

const commandGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
  marginBottom: "24px",
};

const commandCard = {
  background: "linear-gradient(180deg, #121212, #0b0b0b)",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "20px",
};

const commandLabel = {
  color: "#fff",
  fontWeight: "bold",
  marginBottom: "10px",
};

const commandText = {
  color: "#cfcfcf",
  lineHeight: "1.7",
};

const card = {
  background: "#111",
  border: "1px solid #222",
  borderRadius: "18px",
  padding: "22px",
};

const innerCard = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "14px",
  padding: "14px",
};

const actionRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "12px",
};

const suggestionBox = {
  background: "#0f1823",
  border: "1px solid #24425f",
  borderRadius: "12px",
  padding: "12px",
  marginTop: "12px",
};

const alertHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

const sectionHeaderRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "16px",
  flexWrap: "wrap",
};

const filterRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const twoColumnAdminGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "12px",
};

const cardHeaderRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "10px",
};

const searchInput = {
  width: "100%",
  background: "#0b0b0b",
  color: "#fff",
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  padding: "12px 14px",
  marginBottom: "16px",
  outline: "none",
  fontSize: "14px",
};

const summaryRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "16px",
};

const summaryPill = {
  background: "#151515",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  padding: "8px 12px",
  color: "#d1d1d1",
  fontSize: "12px",
};

const gapPill = {
  background: "#2b2410",
  border: "1px solid #5b4f1d",
  borderRadius: "999px",
  padding: "6px 10px",
  color: "#ffd27a",
  fontSize: "12px",
  fontWeight: "bold",
};

const rowText = {
  margin: "6px 0",
  color: "#d1d1d1",
  wordBreak: "break-word",
};

const fieldLabel = {
  color: "#d1d1d1",
  fontSize: "13px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const dividerLine = {
  height: "1px",
  background: "#252525",
  margin: "20px 0",
};

const muted = {
  color: "#d1d1d1",
};

const errorBox = {
  background: "#2a1111",
  color: "#ff9a9a",
  padding: "12px 16px",
  borderRadius: "10px",
  marginBottom: "16px",
};

const actionBox = {
  background: "#101c2b",
  color: "#9fd1ff",
  padding: "12px 16px",
  borderRadius: "10px",
  marginBottom: "16px",
  border: "1px solid #24425f",
};

const list = {
  color: "#d1d1d1",
  lineHeight: "1.8",
  paddingLeft: "20px",
};

const getBadgeStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "74px",
  padding: "6px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: "bold",
  background:
    tone === "healthy"
      ? "#11311f"
      : tone === "warning"
      ? "#2b2410"
      : tone === "alert"
      ? "#341313"
      : "#1f1f1f",
  color:
    tone === "healthy"
      ? "#7CFC00"
      : tone === "warning"
      ? "#ffd27a"
      : tone === "alert"
      ? "#ff9a9a"
      : "#d1d1d1",
  border:
    tone === "healthy"
      ? "1px solid #245235"
      : tone === "warning"
      ? "1px solid #5b4f1d"
      : tone === "alert"
      ? "1px solid #6f2a2a"
      : "1px solid #333",
});

const getFilterButtonStyle = (active) => ({
  borderRadius: "999px",
  padding: "8px 12px",
  border: active ? "1px solid #7CFC00" : "1px solid #333",
  background: active ? "#11311f" : "#161616",
  color: active ? "#7CFC00" : "#d1d1d1",
  fontSize: "12px",
  fontWeight: "bold",
  cursor: "pointer",
});

const getActionButtonStyle = (tone) => ({
  borderRadius: "12px",
  padding: "10px 14px",
  border:
    tone === "alert"
      ? "1px solid #6f2a2a"
      : tone === "warning"
      ? "1px solid #5b4f1d"
      : "1px solid #333",
  background:
    tone === "alert"
      ? "#341313"
      : tone === "warning"
      ? "#2b2410"
      : "#171717",
  color:
    tone === "alert"
      ? "#ffb1b1"
      : tone === "warning"
      ? "#ffd27a"
      : "#d1d1d1",
  fontSize: "12px",
  fontWeight: "bold",
  cursor: "pointer",
});

export default AdminDashboard;

